import React from 'react';
import { Helmet } from 'react-helmet-async';
import AppointmentForm from '@/components/forms/AppointmentForm';

const PorcelainVeneers = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Porcelain Veneers Fort Worth TX | Cosmetic Dentistry | Fort Worth Dental</title>
        <meta name="description" content="Transform your smile with porcelain veneers in Fort Worth TX. Custom dental veneers for a perfect smile. Cosmetic dentistry experts. Call (817) 985-3337." />
        <meta name="keywords" content="porcelain veneers fort worth, dental veneers fort worth tx, cosmetic dentistry, smile makeover, tooth veneers, veneer teeth" />
      </Helmet>

      {/* Hero Section */}
      <section 
        className="relative h-[400px] bg-cover bg-center" 
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/images/services/frame-1288-2.png)`
        }}
      >
        <div className="container mx-auto px-4 h-full flex items-center justify-center text-center">
          <div className="text-white max-w-4xl">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Porcelain Veneers in Fort Worth, TX</h1>
            <p className="text-lg sm:text-xl">Transform Your Smile with Custom Veneers</p>
            <p className="text-sm mt-4 opacity-90">Cosmetic Veneers | Smile Makeover | Dental Veneers</p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          
          {/* Purpose and What Are Porcelain Veneers */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/veneers-new-1.jpg" 
                  alt="Porcelain Veneers" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-2.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Is The Purpose of Porcelain Veneers?</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  Porcelain veneers are a wonderful solution for those looking to brighten their smiles. In a society where first impressions are so important, it is our pleasure to offer excellent cosmetic dentistry solutions to give you the smile of your dreams. Porcelain veneers can restore chipped, crooked, and stained teeth to a natural, beautiful look.
                </p>
                
                <h2 className="text-3xl font-bold text-gray-800 mb-4">What Are Porcelain Veneers?</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Porcelain veneers are thin shells fabricated from high-quality dental porcelain that are custom designed for each customer. This allows us to have a tremendous amount of control over the final appearance of your smile.
                </p>
              </div>
            </div>
          </section>

          {/* Advantages of Porcelain Veneers */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <h2 className="text-3xl font-bold text-gray-800 mb-6">
                  What Are The Advantages Of Porcelain Veneers?
                </h2>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">◆</span>
                    <span>The high-quality porcelain will resist staining for many years.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">◆</span>
                    <span>The porcelain looks very natural and will blend in with your natural teeth for a very beautiful smile.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">◆</span>
                    <span>Because of the natural, porcelain color they are essentially invisible and most people will never know you have them.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">◆</span>
                    <span>Veneers are easy to repair in comparison to other treatment options.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">◆</span>
                    <span>Veneers require the removal of a small amount of tooth enamel in comparison to other treatments.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 text-xl">◆</span>
                    <span>The discomfort level of the treatment is relatively low.</span>
                  </li>
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <img 
                  src="/images/services/veneers-new-2.jpg" 
                  alt="Veneer Advantages" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/dentist-examines.jpg'}
                />
              </div>
            </div>
          </section>

          {/* Procedure */}
          <section className="mb-16">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <img 
                  src="/images/services/veneers-new-1.jpg" 
                  alt="Veneer Procedure" 
                  className="rounded-lg shadow-lg w-full"
                  onError={(e) => e.target.src = '/images/services/frame-1288-2.png'}
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                  What Is The Procedure For Porcelain Veneers?
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  During the first visit, your teeth will be prepared for veneers by removing a thin layer of enamel from the surface of the teeth that will receive the veneers. The amount removed is as little as ½ millimeter and is comparable to the thickness of a fingernail. This is the approximate thickness of the veneers that will be placed on your teeth.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-4">
                  At this same appointment, the dentist will make impressions of your teeth so that the laboratory can fabricate your custom veneers. We will also place temporary veneers until yours arrive.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed">
                  Your next appointment will be approximately three weeks later. This is when the dentist will place the veneers and make any last adjustments to them in color or shape. A special chemical will be used to strengthen the bond of the porcelain veneers to your teeth.
                </p>
              </div>
            </div>
          </section>

          {/* Why Choose Fort Worth Dental */}
          <section className="mb-16 bg-gradient-to-br from-teal-50 to-blue-50 p-8 rounded-lg">
            <h2 className="text-3xl font-bold text-gray-800 mb-4 text-center">
              Why Choose Fort Worth Dental for Porcelain Veneers?
            </h2>
            <p className="text-gray-700 text-lg text-center max-w-3xl mx-auto mb-6">
              At Fort Worth Dental, we serve patients throughout Fort Worth, Aledo, Benbrook, and surrounding areas. 
              Our experienced cosmetic dentistry team creates beautiful, natural-looking veneers that transform smiles.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">30+</div>
                <p className="font-semibold">Years Experience</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">★★★★★</div>
                <p className="font-semibold">5-Star Reviews</p>
              </div>
              <div className="text-center">
                <div className="text-4xl text-teal-600 mb-2">✓</div>
                <p className="font-semibold">Custom Designed</p>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="bg-teal-600 text-white p-12 rounded-lg text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">Schedule Your Porcelain Veneers Consultation</h2>
            <p className="text-xl mb-8">Contact Fort Worth Dental to learn more about porcelain veneers in Fort Worth, TX</p>
            <div className="flex flex-col md:flex-row justify-center items-center space-y-4 md:space-y-0 md:space-x-6">
              <a
                href="tel:8179853337"
                className="bg-white text-teal-600 px-8 py-4 rounded-lg text-lg font-bold hover:bg-gray-100"
              >
                Call (817) 985-3337
              </a>
              <a
                href="/#book-appointment"
                className="bg-purple-600 text-white px-8 py-4 rounded-lg text-lg font-bold hover:bg-purple-700"
              >
                Book Appointment
              </a>
            </div>
          </section>

          {/* Related Blog Post - Internal Link for SEO */}
          <section className="mb-16 bg-purple-50 border border-purple-200 p-8 rounded-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">⭐ NEW: First Fit Veneers - Same-Day Smile!</h2>
            <p className="text-gray-700 mb-4">
              Experience the future of veneers! Get a brand-new smile in as little as 2 hours with our revolutionary First Fit Veneers technology.
            </p>
            <a 
              href="/first-fit-veneers" 
              className="inline-flex items-center text-teal-600 font-semibold hover:text-teal-800"
            >
              Learn About First Fit Veneers →
            </a>
          </section>

          {/* Appointment Form */}
          <section>
            <div className="max-w-2xl mx-auto">
              <h2 className="text-3xl font-bold text-gray-800 mb-6 text-center">
                Schedule Your Porcelain Veneers Appointment in Fort Worth
              </h2>
              <p className="text-gray-600 text-center mb-8">
                Serving Fort Worth, Aledo, Benbrook, and surrounding communities. Call us today or fill out the form below.
              </p>
              <AppointmentForm />
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default PorcelainVeneers;